import { type IconName, icon } from './icons';

/* ---- Illustrative product UIs (fictional data only) ---- */
const labelChip = (c: string, t: string): string => `<span class="lchip ${c}">${t}</span>`;
function mockShell(title: string, body: string, alt: string): string {
  return `<div class="mini" role="img" aria-label="Illustrative product UI: ${alt}"><div class="mini-h"><b>${title}</b><span class="illus">Illustrative product UI</span></div>${body}</div>`;
}
const mrow = (iconName: IconName, left: string, right: string, sub = ''): string =>
  `<div class="mini-r"><span class="l">${icon(iconName)}<span>${left}${sub ? `<small class="ms">${sub}</small>` : ''}</span></span>${right}</div>`;
const flow = (steps: readonly string[]): string =>
  `<ol class="flow">${steps.map((s, i) => `<li class="${i === steps.length - 1 ? 'hl' : ''}">${s}</li>`).join('')}</ol>`;

export const MOCKS = {
  intake: (): string =>
    mockShell(
      'Intake queue · Harborline Industrial Partners',
      `<div class="srcs">${['Email', 'Document', 'Upload', 'Data'].map((s) => `<span>${s}</span>`).join('')}</div>
     ${flow(['Extract', 'Understand', 'Match', 'Validate', 'Route', 'Human review when required'])}
     ${mrow('file', '{{Y}} Assessment Notice.pdf', labelChip('done', 'Matched: {{CASE}}'), 'Classified: Assessment notice · confidence 0.97')}
     ${mrow('file', 'Rent roll Q2.xlsx', labelChip('run', 'Requested'), 'Rent roll · 0.91 · Missing: signed owner authorization')}`,
      'intake queue. "{{Y}} Assessment Notice.pdf" classified as an assessment notice at confidence 0.97 and matched to case {{CASE}}. "Rent roll Q2.xlsx" classified as a rent roll at 0.91; a signed owner authorization is missing and has been requested.',
    ),
  docs: (): string =>
    mockShell(
      'Document Intelligence · {{Y}} Assessment Notice.pdf',
      `${flow(['Document', 'OCR / extraction', 'Classification', 'Property / case match', 'Confidence', 'Routing', 'Human review'])}
     <div class="fields">
       <div><small>Parcel ID</small><b class="mono">74-219-083-014</b></div>
       <div><small>Jurisdiction</small><b>Cook County, IL</b></div>
       <div><small>Tax year</small><b>{{Y}}</b></div>
       <div><small>Property</small><b>Harborline Distribution Center 3</b></div>
     </div>
     ${mrow('checkCircle', '14 fields extracted', labelChip('done', 'Confidence 0.97'))}`,
      'a {{Y}} assessment notice with extracted fields: parcel 74-219-083-014, Cook County, Illinois, tax year {{Y}}, Harborline Distribution Center 3. Fourteen fields extracted at confidence 0.97.',
    ),
  settlement: (): string =>
    mockShell(
      'Settlement · Case {{CASE}}',
      `<ol class="tline">
       <li class="done"><b>Offer received</b><small>{{OFFER}}</small></li>
       <li class="done"><b>Counteroffer prepared</b><small>{{COUNTER}} · supporting information attached</small></li>
       <li class="human"><b>Professional review</b><small>Morgan Lee · final decision stays with the professional</small></li>
       <li class="run"><b>Status: Under negotiation</b><small>Case history and related documents linked</small></li>
     </ol>`,
      'settlement timeline for case {{CASE}}. Offer received {{OFFER_L}}; counteroffer prepared {{COUNTER_L}}; professional review by Morgan Lee; status under negotiation.',
    ),
  portal: (): string =>
    mockShell(
      'Client portal · Harborline Industrial Partners',
      `<div class="pnav">${['Portfolio', 'Cases', 'Documents', 'Deadlines', 'Savings', 'Reports', 'Messages'].map((t, i) => `<span class="${i === 0 ? 'on' : ''}">${t}</span>`).join('')}</div>
     <div class="fields">
       <div><small>Portfolio</small><b>412 properties</b></div>
       <div><small>Parcels</small><b>1,036</b></div>
       <div><small>Next deadline</small><b>{{DL_S}}</b></div>
       <div><small>Case in review</small><b class="mono">{{CASE}}</b></div>
     </div>`,
      'client portal for Harborline Industrial Partners with navigation for Portfolio, Cases, Documents, Deadlines, Savings, Reports and Messages; 412 properties, 1,036 parcels; next deadline {{DL_L}}.',
    ),
  billing: (): string =>
    mockShell(
      'Billing &amp; Financials',
      `${mrow('receipt', 'Invoice {{INV}}', labelChip('done', 'Sent'), 'Contingency fee · Case {{CASE}}')}
     ${mrow('dollar', 'Fee tracking', labelChip('queued', 'Linked to case financials'))}
     ${mrow('checkCircle', 'Payment status', labelChip('run', 'Awaiting payment'))}`,
      'billing view with invoice {{INV}}, a contingency fee for case {{CASE}}, payment status sent.',
    ),
  reporting: (): string =>
    mockShell(
      'Reporting · Portfolio view',
      `<div class="hier">${['Organization', 'Office', 'Market / Jurisdiction', 'Client', 'Property', 'Parcel', 'Case'].map((t) => `<span>${t}</span>`).join('<i>→</i>')}</div>
     ${mrow('building', 'Harborline Industrial Partners', labelChip('queued', '412 properties'), '1,036 parcels')}
     ${mrow('pin', 'Cook County, IL', labelChip('run', 'Deadline {{DL_S}}'), 'Harborline Distribution Center 3')}
     ${mrow('file', '{{CASE}}', labelChip('human', 'Evidence Review'), 'Parcel 74-219-083-014')}`,
      'reporting drill-down from organization to office, market, client, property, parcel and case, showing Harborline Industrial Partners, Cook County, Illinois, and case {{CASE}} in evidence review.',
    ),
};
